import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from "react";
import {
  ApiError, getAssignments, getCohort, getEntryQueue, getProgress, getRubrics, getSettings,
  cancelQueued, getSubmissions, getTeacherTests, poll, publishAssignment, queueGrade, reprocessRun,
  type Assignment, type CohortRow, type CourseAccess, type LiveFeed,
  type Overview, type Progress, type QueueResult,
  type SubmissionRow,
} from "../api";
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useShell } from "../Layout";
import { useLive } from "../live";
import { LiveLine } from "../LiveBar";
import {
  isAssignmentTab, isHandedFilter, isLegacyMarking, isMarkingFilter, legacyMarkingHref, routes,
  type AssignmentTab, type HandedFilter, type MarkingFilter,
} from "../routes";
import { type CriterionMode, effectivePoints } from "../rubric";
import { AssignmentSetup } from "./AssignmentSetup";
import { CAN_ENTER, IntoClassroom, useEntryActions } from "./EntryView";
import {
  blankTally, FILTER_NOUN, isEntered, isParked, joinSubmissions, markOf,
  STATUS_HINT, tallies, isReadyRow, workRank, type MarkRow, type Tally,
} from "./marking";
import { ReportHref, ReportLists, ReportSheet, useReportList, useReportListsHost } from "./ReportSheet";
import { CAN_RUN, RunCell } from "./RunCell";
import { criterionOf } from "./TeacherTests";
import { tracked, useAsync, type Async } from "../useAsync";
import {
  Badge, Card, CardBody, DASH, Empty, ErrorBox, Freshness, Markdown, State, Th,
  When, Why, sorted, type Sort,
} from "../ui";


/**
 * What a read-only cohort means here: what the page-head badge's Why button says.
 * ⚠️ THE OLD SENTENCE SAID THE LIST "LIVES IN CODE, NOT IN THE DATABASE" — false since
 * 2026-09-06, when writability became a setting floored by `NEVER_WRITABLE`.
 */
const READ_ONLY_NOTE =
  "Nothing here may write a grade, a release or an announcement to Classroom. Reading, "
  + "grading and recording a mark you typed in by hand are unaffected.";

/** How long a row that has just graded stays lit. */
export const JUST_NOW_MS = 60_000;

/**
 * The submissions that graded in the last minute, and when — so a row can say GRADED JUST NOW.
 * ⚠️ A CONTEXT, BECAUSE FOUR COMPONENTS SIT BETWEEN THE PAGE THAT HEARS IT AND THE ROW THAT SAYS
 * IT, and threading a map through each would make every one of them about something it is not.
 */
const JustNow = createContext<Map<string, number>>(new Map());

/**
 * The one marking page: every assignment, every grade, the marks to type into Classroom, and what
 * is running — with no tabs.
 *
 * 🔴 GRADES AND ENTRY STOPPED BEING PAGES (2026-10-03), AND THEN STOPPED BEING TABS (owner,
 * 2026-10-06: *"insteated of having assimgnets grades and entry tabs lets put everhtin on
 * assimgents"*). The assignments are a list on the left, and the chosen one is on the right with
 * Overview, Handed in and Configure (owner, 2026-10-13).
 * 🔴 NO COHORT-WIDE COUNTS (owner, 2026-10-13: *"no global needed"*). In review, Ready, Released
 * or entered and Held belong to one assignment: its Overview's tiles count them, and each opens
 * its Handed in list under the chip of the same name. The list on the left shows how many of each
 * assignment are waiting for review, in orange. The tab is in the address (`?tab=`) and so is the
 * chip (`?show=`), so a filtered list is a link; `?show=` with no assignment open — an alarm from
 * the Overview, an old Grades link — opens the first assignment that has any.
 * 🔴 TO ENTER IS GONE (2026-10-13): a mark to type into Classroom is a Ready one, and under the
 * Ready chip each row carries Copy note, Open in Classroom and Mark entered.
 *
 * 🔴 A COUNT IS THE COUNT OF WHAT IT OPENS. A tile is the length of the rows its chip lists, both
 * built from the database's grades (`marking.tallies`, `rows`), so the two cannot disagree.
 * 🔴 EVERY COUNT BUT ONE IS THE DATABASE'S. `GET /cohort` and `GET /entry-queue` are database
 * reads and answer in milliseconds; the assignment listing is a Classroom call, fetched because it
 * is what refreshes the title cache every other page reads, and it decides nothing here but the
 * list. ⚠️ HANDED IN IS CLASSROOM'S, from the poller's snapshot (`GET /progress`), and says how
 * old.
 * 🔴 NOTHING HERE NEEDS A REFRESH. The live feed says what is running, and the moment a run lands
 * the database's lists are read again — never the Classroom listing, which is seconds and in which
 * nothing moved.
 *
 * ⚠️ WHICH ASSIGNMENT IS OPEN IS IN THE PATH (`/assignments/:cw`), so an alarm about a parked
 * submission lands on the assignment it is about with its list already showing — and only one
 * is open at a time, because each listing is a round trip to Google.
 */
export function AssignmentsTab() {
  const { courseId, overview, access } = useShell();
  const { cw: openParam } = useParams();
  const [params] = useSearchParams();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const live = useLive();
  // 🔴 `?show=` ON THE BARE PAGE NAMES A FILTER AND NO ASSIGNMENT — a link from the Overview
  // page ("65 grades need review"), or an address from before 2026-10-13, when a filter was the
  // whole cohort's. It opens the first assignment that has any, on Handed in, filtered.
  const asked = params.get("show") ?? "";
  const wanted: MarkingFilter | null = !openParam && isMarkingFilter(asked) ? asked : null;

  const a = useAsync(() => getAssignments(courseId), [courseId]);
  const g = useAsync(() => getCohort(courseId), [courseId]);
  const e = useAsync(() => getEntryQueue(courseId), [courseId]);
  const r = useAsync(() => getRubrics(courseId), [courseId]);
  const s = useAsync(() => getSettings(courseId), [courseId]);
  const p = useAsync(() => getProgress(courseId), [courseId]);
  const [order, setOrder] = useOrder();

  // ⚠️ A MARK CHANGED, SO THE DATABASE'S LISTS AND THE SHELL'S COUNTS ARE STALE — and the
  // Classroom listing is not: reloading it would spend a Google round trip to learn nothing.
  const marksChanged = () => { g.reload(); e.reload(); overview.reload(); };
  // 🔴 THE LIST CHANGED — a setup saved, a rescan ran, a draft was published — SO THE OPEN
  // ASSIGNMENT'S OWN LISTING IS STALE TOO. It is a fetch of its own, keyed on the assignment, and
  // nothing else reloaded it: after a change archived every grade, its rows went on showing the
  // archived marks and queue reasons from before (2026-10-03).
  const [listVersion, setListVersion] = useState(0);
  const listChanged = () => { a.reload(); setListVersion((n) => n + 1); marksChanged(); };

  // 🔴 A RUN LANDED, SO THE DATABASE'S LISTS MOVED (2026-10-06): read them again — milliseconds —
  // and light the row it landed on for a minute.
  const lastEvent = useRef(0);
  const [justNow, setJustNow] = useState<Map<string, number>>(() => new Map());
  const reloadGrades = g.reload;
  const reloadEntry = e.reload;
  useEffect(() => {
    const fresh = live.events.filter((ev) => ev.n > lastEvent.current);
    if (!fresh.length) return;
    lastEvent.current = fresh[fresh.length - 1]!.n;
    reloadGrades(); reloadEntry();
    const landed = fresh.filter((ev) => ev.item.outcome === "recorded");
    if (landed.length) {
      setJustNow((m) => {
        const next = new Map(m);
        for (const ev of landed) next.set(ev.item.submission_id, ev.seenAt);
        return next;
      });
    }
  }, [live.events, reloadGrades, reloadEntry]);
  // The light goes out a minute after it came on — one timer, for the soonest.
  useEffect(() => {
    if (!justNow.size) return;
    const soonest = Math.min(...[...justNow.values()].map((at) => at + JUST_NOW_MS - Date.now()));
    const t = window.setTimeout(() => setJustNow((m) => new Map(
      [...m].filter(([, at]) => Date.now() - at < JUST_NOW_MS))), Math.max(0, soonest));
    return () => window.clearTimeout(t);
  }, [justNow]);

  // The title an assignment goes by: Classroom's when the listing has answered, otherwise the
  // console's cache as `GET /cohort` carries it.
  const titles = useMemo(() => {
    const out = new Map<string, string>();
    for (const row of g.data?.rows ?? []) {
      if (row.assignment_title) out.set(row.course_work_id, row.assignment_title);
    }
    for (const cw of a.data?.assignments ?? []) out.set(cw.course_work_id, cw.title);
    return out;
  }, [a.data, g.data]);
  const listed = useMemo(() => {
    const all = a.data?.assignments ?? [];
    return order === "oldest" ? [...all].reverse() : all;
  }, [a.data, order]);

  // ⚠️ ONE "as of" FOR THE PAGE: the oldest of what it shows, and Refresh asks every source
  // again — the Classroom listing included, which is what a Refresh of this list has always
  // done.
  const page: Async<unknown> = {
    data: null, error: null,
    loading: a.loading || g.loading || e.loading,
    fetchedAt: [a.fetchedAt, g.fetchedAt, e.fetchedAt].every((t) => t != null)
      ? Math.min(a.fetchedAt!, g.fetchedAt!, e.fetchedAt!) : null,
    reload: () => { a.reload(); g.reload(); e.reload(); p.reload(); },
  };

  // 🔴 WHETHER A TEACHER'S RUN GOES IS THE SERVER'S, WITH ITS OWN SENTENCE — never a copy of the
  // rule here, so a disabled Run and the reason beside it cannot disagree.
  // ⚠️ A HOLD ALWAYS CARRIES A SENTENCE: an empty one would disable Run all and leave each row's
  // Run on, because one reads "held or not" and the other reads the words.
  const usage = overview.data?.usage.grading;
  const held = usage?.teacher_run_goes === false
    ? (usage.teacher_run || "The server is holding a teacher's Run just now.") : null;
  // 🔴 A REPORT OPENS OVER THIS PAGE, AT THIS PAGE'S OWN ADDRESS, so its filter and its open
  // assignment are what the sheet's ‹ › walk, and closing it leaves the reader where they were.
  const reportHref = useCallback((sid: string) => routes.report(courseId, sid, { pathname, search }),
                                 [courseId, pathname, search]);
  const [setList, list] = useReportListsHost();

  // 🔴 AN ADDRESS FROM BEFORE THE ONE PAGE LANDS ON WHAT IT MEANT: `?view=grades&status=REVIEW` on
  // In review, `?view=entry` and `?show=enter` (retired 2026-10-13) on Ready, `?cw=` on that
  // assignment. They are in bookmarks and pasted into chat.
  if (isLegacyMarking(search)) {
    return <Navigate replace to={legacyMarkingHref(courseId, search, openParam ?? "")} />;
  }
  if (wanted) {
    const to = firstWith(wanted, listed, g.data && e.data ? tallies(g.data.rows, e.data.rows) : null,
                         live.feed, a.data != null);
    // Still reading what decides it: say nothing rather than open the wrong assignment.
    if (to === undefined) return <Card><CardBody><div className="skel" /></CardBody></Card>;
    const report = params.get("report");
    const href = to ? routes.assignment(courseId, to, "handed", wanted) : routes.page(courseId, "assignments");
    return <Navigate replace to={report ? `${href}${href.includes("?") ? "&" : "?"}report=${encodeURIComponent(report)}` : href} />;
  }

  return (
    <ReportLists.Provider value={setList}>
    <ReportHref.Provider value={reportHref}>
    <JustNow.Provider value={justNow}>
    <ReportSheet courseId={courseId} list={list} />
    <div className="stack">
      <div className="page-head">
        <p className="eyebrow">Cohort</p>
        <h2>Assignments</h2>
        {/* 🔴 A BADGE ON THE TITLE'S LINE, NOT A BOX ABOVE THE PAGE (owner, 2026-10-03: *"just a
            badge on line with Assignments title: read only!"*). It is a fact about the cohort
            that no control on the page implies, so it stays on the page — but as a word beside
            the title, with the sentence behind a Why button rather than two lines of amber
            prose above everything the page is for.
            🔴 NOT A `title` OR AN `aria-description`. A title cannot be reached on a tap, and an
            aria-description reaches only some screen readers — the report's escalation badge
            was refused for exactly that and uses `Why`, which a tap, a focus or a hover opens.
            ⚠️ A disabled control still says why beside it (Publish, Release): that is a
            different rule, and it stays. */}
        {!access.write_allowed && (
          <span className="head-badge">
            <span className="badge stop">read-only</span>
            <Why label="Why this cohort is read-only">{READ_ONLY_NOTE}</Why>
          </span>)}
        {/* ⚠️ TWO FACTS, SIDE BY SIDE: whether what runs is current (the live line), and how old
            the lists are (Refresh asks every source again, the Classroom listing included). */}
        <span className="fresh"><LiveLine /><Freshness q={page} /></span>
      </div>

      {/* 🔴 A MISMATCH IS THE ONE FAILURE THAT LOOKS LIKE NOTHING: the console would score
          against a rubric the agent never saw, and every citation would still resolve.
          ⚠️ `unavailable` is reported as itself and never as agreement — an absent check
          must be visible as absent. */}
      {r.data?.verified.state === "mismatch" && (
        <p className="alert refused small" role="alert">
          ⛔ <strong>This console holds a different rubric from the one the grading agent
          reads.</strong> {r.data.verified.mismatched.join("; ")}. Nothing here is safe to
          grade with until that is resolved: run <code>sync-rubrics.sh</code>, redeploy the
          console, and re-run <code>apply-grader.sh</code> so both sides come from the same
          file.
        </p>)}
      {r.data?.verified.state === "unavailable" && (
        <p className="alert small">
          ⚠️ <strong>Could not check these against what the agent reads.</strong>{" "}
          {r.data.verified.unavailable.join("; ")}. The rubrics ship with the console and
          grading works; what is missing is the second opinion, not the rubric.
        </p>)}

      {/* 🔴 A FAILED READ SAYS SO (2026-10-03). Every count here is the database's, so with
          `/cohort` or `/entry-queue` down every cell was a dash — the same dash as a read still
          on its way — and nothing on this page said which. */}
      {g.error && <ErrorBox err={g.error} onRetry={g.reload} />}
      {e.error && <ErrorBox err={e.error} onRetry={e.reload} />}

      {/* 🔴 NO STRIP ACROSS THE TOP (owner, 2026-10-13: *"no global needed"*). Every count on
          this page is the chosen assignment's, on its Overview and as its Handed in filters. */}
      <AssignmentsView courseId={courseId} access={access} a={a} g={g} e={e} r={r} s={s}
                       p={p} overview={overview.data} open={openParam ?? null}
                       titles={titles} listVersion={listVersion} listed={listed}
                       order={order} setOrder={setOrder} held={held}
                       onPick={(cw) => navigate(routes.assignment(courseId, cw))}
                       onListChanged={listChanged}
                       onMarksChanged={marksChanged} />
    </div>
    </JustNow.Provider>
    </ReportHref.Provider>
    </ReportLists.Provider>
  );
}

/**
 * The assignment a filter named on the bare page opens: the first listed — published first, in the
 * page's own order — with any rows under it; `null` when none has (the page, unfiltered);
 * `undefined` while what decides it is still being read.
 * ⚠️ RUNNING IS THE LIVE FEED'S, every other filter the database's — the same sources the
 * assignment's own counts read, so the one it opens never shows a 0 for the filter that sent you.
 */
export function firstWith(f: MarkingFilter, listed: Assignment[], per: Map<string, Tally> | null,
                          feed: LiveFeed | null, listRead: boolean): string | null | undefined {
  if (!listRead) return undefined;
  const has = (cw: string): boolean | undefined => {
    if (f === "running") {
      if (!feed) return undefined;
      return [...feed.runs, ...feed.queued].some((x) => x.course_work_id === cw);
    }
    if (!per) return undefined;
    return ((per.get(cw) ?? blankTally())[f] ?? 0) > 0;
  };
  for (const x of listed) {
    const h = has(x.course_work_id);
    if (h === undefined) return undefined;
    if (h) return x.course_work_id;
  }
  return null;
}

/** One assignment's own counts, as its Overview's tiles. Each opens its Handed in list under the
 *  chip of the same name. */
const COUNTS: { k: "review" | "ready" | "done" | "held"; label: string; hint: string }[] = [
  { k: "review", label: "in review", hint: STATUS_HINT.REVIEW! },
  { k: "ready", label: "ready",
    hint: "measured and reviewed — release it, or type it into Classroom and mark it entered" },
  { k: "done", label: "released or entered",
    hint: "the student has the mark: released by this console, or typed into Classroom by hand" },
  { k: "held", label: "held", hint: STATUS_HINT.HELD! },
];

/** What the handed-in cell says when `GET /progress` did not answer.
 *
 *  ⚠️ A REFUSAL IS NOT AN OUTAGE. A demo cohort's `/progress` answers 400 "Refused: … is a demo
 *  cohort, so there is nothing in Classroom to list …" — deliberately, and the route keeps it
 *  away from the Infrastructure label for exactly that reason. Rendering it as "Classroom could
 *  not be read just now" stated a decision as a fault (2026-10-03). The server's sentence is the
 *  only copy that cannot drift from what was decided, so a refusal is rendered as itself — its
 *  first sentence, in a cell three lines tall, with the whole of it as the cell's title. */
function handedInError(err: ApiError): string {
  if (err.kind === "infrastructure" || err.status >= 500) {
    return "Classroom could not be read just now";
  }
  return err.message.split(/(?<=\.)\s+/)[0] || err.message;
}

/**
 * Newest or oldest first (owner, 2026-10-05: a toggle on the card), remembered in this browser.
 * 🔴 THE SERVER OWNS THE ORDER — drafts first, then by when each was published, which only the
 * sweep's snapshot knows (`_in_publish_order`) — so Oldest first is that list reversed, never a
 * second sort here that could disagree with it about what "published" means. Reversed, drafts
 * come last, after the newest.
 * ⚠️ Storage may be missing or throw (a private window, blocked site data), so every read and
 * write is guarded and the page falls back to newest first.
 */
const ORDER_KEY = "tc-assignment-order";
type Order = "newest" | "oldest";
function useOrder(): [Order, (o: Order) => void] {
  const [o, setO] = useState<Order>(() => {
    try { return window.localStorage.getItem(ORDER_KEY) === "oldest" ? "oldest" : "newest"; }
    catch { return "newest"; }
  });
  return [o, (n: Order) => {
    setO(n);
    try { window.localStorage.setItem(ORDER_KEY, n); } catch { /* a convenience only */ }
  }];
}

/**
 * Handed in: every assignment in a column on the left, and the chosen one on the right (owner,
 * 2026-10-13, approved mockup https://claude.ai/artifact/VHpRtGZ9Smt9Mf5QozaQ3B).
 *
 * 🔴 THE CHOSEN ONE IS THE ONE IN THE PATH, OR THE FIRST PUBLISHED ONE LISTED — the newest, or
 * the oldest with the order turned round. Choosing another is a link, so Back undoes it.
 * ⚠️ ON A PHONE THE COLUMN IS A SELECT above the assignment: a list of seven tall rows would push
 * the assignment itself a screen down.
 */
function AssignmentsView({ courseId, access, a, g, e, r, s, p, overview, open, titles,
                           listVersion, listed, order, setOrder, held, onPick, onListChanged,
                           onMarksChanged }: {
  courseId: string; access: CourseAccess;
  a: Async<{ assignments: Assignment[] }>;
  g: Async<Awaited<ReturnType<typeof getCohort>>>;
  e: Async<Awaited<ReturnType<typeof getEntryQueue>>>;
  r: Async<Awaited<ReturnType<typeof getRubrics>>>;
  s: Async<Awaited<ReturnType<typeof getSettings>>>;
  p: Async<Progress>;
  overview: Overview | null; open: string | null; titles: Map<string, string>;
  listVersion: number; listed: Assignment[]; order: Order; setOrder: (o: Order) => void;
  held: string | null;
  onPick: (cw: string) => void;
  onListChanged: () => void; onMarksChanged: () => void;
}) {
  const per = useMemo(
    () => (g.data && e.data ? tallies(g.data.rows, e.data.rows) : null), [g.data, e.data]);
  const parked = useMemo(() => new Map((overview?.assignments ?? []).map(
    (x) => [x.course_work_id, Object.values(x.queue.parked).reduce((n, v) => n + v, 0)])),
    [overview]);
  const tallyOf = (cw: string) => per?.get(cw) ?? (per ? blankTally() : null);
  // ⚠️ NOT A DRAFT, unless there is nothing else: drafts are listed first, and a draft has no
  // submissions to open — the newest published assignment is what somebody comes here for.
  const chosen = open ?? (listed.find((x) => x.state !== "DRAFT") ?? listed[0])?.course_work_id
    ?? null;
  const cw = a.data?.assignments.find((x) => x.course_work_id === chosen) ?? null;
  const unlisted = a.data != null && open != null && cw == null;

  return (
    <div className="asg-two">
    <div className="asg-two-in">
      <aside className="card asg-side" aria-label="Coursework">
        <div className="asg-side-head">
          <h3>Coursework</h3>
          <span className="spacer" />
          <div className="seg" role="group" aria-label="Order of the assignments">
            <button type="button" aria-pressed={order === "newest"}
                    onClick={() => setOrder("newest")}>Newest</button>
            <button type="button" aria-pressed={order === "oldest"}
                    onClick={() => setOrder("oldest")}>Oldest</button>
          </div>
        </div>
        {a.error ? <div className="asg-side-body"><ErrorBox err={a.error} onRetry={a.reload} /></div>
          : !a.data ? <div className="asg-side-body"><div className="skel" /></div>
          : listed.length === 0
            ? <div className="asg-side-body"><Empty title="No coursework in this cohort"
                hint="Make an assignment in Classroom and it appears here — though only
                      bot-created coursework can ever be written back to." /></div>
            : <>
                {/* ⚠️ ON A PHONE, A SELECT — the same choice, without a screen of rows above it. */}
                <label className="asg-pick">
                  <span className="sr">Assignment</span>
                  <select className="select" value={chosen ?? ""} aria-label="Assignment"
                          onChange={(ev) => onPick(ev.target.value)}>
                    {listed.map((x) => (
                      <option key={x.course_work_id} value={x.course_work_id}>{x.title}</option>))}
                  </select>
                </label>
                <ol className="asg-side-list">
                  {listed.map((x) => (
                    <SideItem key={x.course_work_id} courseId={courseId} cw={x}
                              tally={tallyOf(x.course_work_id)}
                              current={chosen === x.course_work_id} />))}
                </ol>
              </>}
        {/* ⚠️ THE STRIP USED TO SAY WHEN CLASSROOM'S HANDED-IN COUNTS COULD NOT BE READ; with it
            gone, the list's foot does — a refusal as itself, an outage as one. */}
        {p.error && (
          <p className="asg-side-note small muted" title={p.error.message}>
            Handed in: {handedInError(p.error)}</p>)}
      </aside>

      <div className="asg-col">
        {unlisted
          ? <Card><CardBody>
              <Unlisted cw={open} title={titles.get(open) ?? ""}
                        grades={g.data ? g.data.rows.filter((x) => x.course_work_id === open)
                                       : null} />
            </CardBody></Card>
          : cw
            ? <AssignmentDetail key={cw.course_work_id} courseId={courseId} cw={cw}
                access={access} tally={tallyOf(cw.course_work_id)}
                handed={p.data?.by_assignment[cw.course_work_id]?.submitted}
                parked={parked.get(cw.course_work_id) ?? 0}
                g={g} e={e} r={r} s={s} listVersion={listVersion} held={held}
                onListChanged={onListChanged} onMarksChanged={onMarksChanged} />
            : a.data && listed.length > 0 && <Card><CardBody><div className="skel" /></CardBody></Card>}
      </div>
    </div>
    </div>
  );
}

/** What makes an assignment need somebody, in words: a grade waiting for review, a mark ready to
 *  go out (for somebody who may send it), or a submission parked. */
function needsOf(tally: Tally | null, parked: number, access: CourseAccess): string[] {
  return [
    tally && tally.review > 0 ? `${tally.review} to review` : "",
    tally && tally.ready > 0 && access.can_release ? `${tally.ready} ready` : "",
    parked > 0 ? `${parked} parked` : "",
  ].filter(Boolean);
}

/**
 * One assignment in the column: its title, and in orange how many of its grades wait for review.
 * 🔴 THE REVIEW COUNT, NOT THE HANDED-IN ONE (owner, 2026-10-13: *"put in orange the ones that need
 * review, number instead of the handed in"*). Nothing to review is no badge, never a 0, and the
 * amber edge went with the handed-in number: the orange number says what the edge said.
 */
function SideItem({ courseId, cw, tally, current }: {
  courseId: string; cw: Assignment; tally: Tally | null; current: boolean;
}) {
  const n = tally?.review ?? 0;
  return (
    <li>
      <Link className="asg-side-it" to={routes.assignment(courseId, cw.course_work_id)}
            aria-current={current ? "true" : undefined}>
        <span className="asg-side-t">{cw.title}
          {cw.state === "DRAFT" && <>{" "}<Badge tone="held">draft</Badge></>}</span>
        {n > 0 && (
          <Badge tone="warn" title={`${n} waiting for your review`}>
            {n}<span className="sr"> to review</span></Badge>)}
      </Link>
    </li>);
}

/**
 * The chosen assignment: Overview, Handed in and Configure (owner, 2026-10-13: *"the first one
 * overview, the brief and above the brief anything that makes sense, then assignments delivered
 * and then tab config"*).
 *
 * 🔴 THE TAB IS IN THE ADDRESS (`?tab=handed|configure`; none is the Overview), so a link names
 * it — an alarm about a parked submission opens on Handed in, with the list showing.
 * 🔴 HANDED IN AND CONFIGURE ARE MOUNTED THE FIRST TIME THEY ARE OPENED AND THEN KEPT. The listing
 * is a Classroom round trip, so the Overview does not pay for it; and Configure holds an unsaved
 * draft, which a look at the Overview and back must not throw away.
 */
function AssignmentDetail({ courseId, cw, access, tally, handed, parked, g, e, r, s, listVersion,
                            held, onListChanged, onMarksChanged }: {
  courseId: string; cw: Assignment; access: CourseAccess; tally: Tally | null;
  handed: number | undefined; parked: number;
  g: Async<Awaited<ReturnType<typeof getCohort>>>;
  e: Async<Awaited<ReturnType<typeof getEntryQueue>>>;
  r: Async<Awaited<ReturnType<typeof getRubrics>>>;
  s: Async<Awaited<ReturnType<typeof getSettings>>>;
  listVersion: number; held: string | null;
  onListChanged: () => void; onMarksChanged: () => void;
}) {
  const [params, setParams] = useSearchParams();
  const asked = params.get("tab") ?? "";
  // 🔴 A FILTER IS ON HANDED IN BY DEFINITION (2026-10-13): `?show=` is that list's, so it opens
  // the tab whatever `?tab=` says.
  const tab: "overview" | AssignmentTab = isHandedFilter(params.get("show") ?? "") ? "handed"
    : isAssignmentTab(asked) ? asked : "overview";
  const [seen, setSeen] = useState<Set<string>>(() => new Set([tab]));
  useEffect(() => { setSeen((x) => (x.has(tab) ? x : new Set([...x, tab]))); }, [tab]);
  const show = (t: "overview" | AssignmentTab) => {
    const next = new URLSearchParams(params);
    if (t === "overview") next.delete("tab"); else next.set("tab", t);
    // ⚠️ LEAVING HANDED IN LEAVES ITS FILTER: it would otherwise pull the reader straight back.
    if (t !== "handed") next.delete("show");
    setParams(next);
  };
  const live = useLive();
  const running = [...(live.feed?.runs ?? []), ...(live.feed?.queued ?? [])]
    .filter((x) => x.course_work_id === cw.course_work_id).length;
  const needs = needsOf(tally, parked, access);
  const due = dueOf(cw.due);
  const published = dayOf(cw.published_at);
  const tabs: { id: "overview" | AssignmentTab; label: ReactNode }[] = [
    { id: "overview", label: "Overview" },
    { id: "handed", label: <>Handed in{handed != null && <span className="count">{handed}</span>}</> },
    { id: "configure", label: "⚙ Configure" },
  ];
  const ids = tabs.map((t) => t.id);
  return (
    <section className="card" aria-label={cw.title}>
      <header className="asg-dhead">
        {/* 🔴 THE BADGES ARE ON THE TITLE'S LINE (owner, 2026-10-13: "enter by hand … put in
            front of the title"), each wrapping like a word, and Classroom ↗ at the line's end. */}
        <div className="asg-dhead-row">
          <div className="asg-dline">
            <h3 className="asg-dtitle">{cw.title}</h3>
            <span className="asg-dbadges">
              {cw.state === "DRAFT" && <Badge tone="held" title="Not published to students yet.
Classroom holds no submissions for a draft.">draft</Badge>}
              {needs.length > 0 && <Badge tone="warn" dot title={needs.join(" · ")}>needs you</Badge>}
              <Badges cw={cw} b={cw.binding} />
            </span>
          </div>
          {cw.alternate_link && <a className="btn sm asg-dlink" href={cw.alternate_link}
                                   target="_blank" rel="noreferrer">Classroom ↗</a>}
        </div>
        {/* ⚠️ NO "100 POINTS" (owner, same day: "no need for that"). */}
        <div className="asg-sub small muted">
          {published && <><span className="nowrap">published {published}</span>{" · "}</>}
          <span className="nowrap">{due ? <>due {due}</> : "no due date"}</span>
        </div>
      </header>

      <div className="tabs asg-dtabs" role="tablist" aria-label="This assignment"
           onKeyDown={(ev) => {
             if (ev.key !== "ArrowLeft" && ev.key !== "ArrowRight") return;
             ev.preventDefault();
             const i = ids.indexOf(tab);
             const n = ids[(i + (ev.key === "ArrowRight" ? 1 : ids.length - 1)) % ids.length]!;
             show(n);
             document.getElementById(`asg-tab-${n}`)?.focus();
           }}>
        {tabs.map((t) => (
          <button key={t.id} type="button" role="tab" className="tab" id={`asg-tab-${t.id}`}
                  aria-controls={`asg-pane-${t.id}`} aria-selected={tab === t.id}
                  tabIndex={tab === t.id ? 0 : -1} onClick={() => show(t.id)}>{t.label}</button>))}
      </div>

      <div className="asg-pane" id="asg-pane-overview" role="tabpanel"
           aria-labelledby="asg-tab-overview" hidden={tab !== "overview"}>
        <AssignmentOverview courseId={courseId} cw={cw} access={access} tally={tally}
                            parked={parked} running={running} r={r} s={s}
                            onShow={show} />
      </div>
      <div className="asg-pane" id="asg-pane-handed" role="tabpanel"
           aria-labelledby="asg-tab-handed" hidden={tab !== "handed"}>
        {/* 🔴 A DRAFT HAS NO SUBMISSIONS, so Classroom refuses to list any and there is nothing
            to run. Say that where the list would be, rather than asking Google and showing its
            refusal. */}
        {seen.has("handed") && (cw.state === "DRAFT"
          ? <Publish courseId={courseId} cw={cw} access={access} onChanged={onListChanged} />
          : <Submissions courseId={courseId} cw={cw} access={access} g={g} e={e}
                         listVersion={listVersion} held={held} onChanged={onMarksChanged}
                         rescan={<RescanButton courseId={courseId} may={access.can_run_grading}
                                               quiet onDone={onListChanged} />} />)}
      </div>
      <div className="asg-pane" id="asg-pane-configure" role="tabpanel"
           aria-labelledby="asg-tab-configure" hidden={tab !== "configure"}>
        {seen.has("configure") && (r.data && s.data
          ? <AssignmentSetup courseId={courseId} cw={cw}
                    rubrics={r.data.rubrics} defaultRubricId={r.data.default_rubric_id}
                    harnesses={s.data.engines.harnesses}
                    sysEngine={String(s.data.settings.engine ?? "")}
                    sysAuto={Boolean(s.data.settings.auto_grade)}
                    canEdit={access.can_edit_rubrics}
                    onChanged={onListChanged} />
          : r.error ? <ErrorBox err={r.error} onRetry={r.reload} />
          : s.error ? <ErrorBox err={s.error} onRetry={s.reload} />
          : <div className="skel" />)}
      </div>
    </section>
  );
}

/**
 * The Overview of one assignment: what needs you, its counts, how it is marked, then the brief.
 *
 * 🔴 EVERY COUNT OPENS WHAT IT COUNTS: handed in opens the Handed in tab; the grade counts open
 * that filter of the page, narrowed to this assignment.
 * ⚠️ NOTHING HERE ASKS CLASSROOM: the counts are the database's and the poller's snapshot, and the
 * brief arrived with the assignment listing.
 */
function AssignmentOverview({ courseId, cw, access, tally, parked, running, r, s,
                              onShow }: {
  courseId: string; cw: Assignment; access: CourseAccess; tally: Tally | null;
  parked: number; running: number;
  r: Async<Awaited<ReturnType<typeof getRubrics>>>;
  s: Async<Awaited<ReturnType<typeof getSettings>>>;
  onShow: (t: "overview" | AssignmentTab) => void;
}) {
  // 🔴 EVERY COUNT OPENS THIS ASSIGNMENT'S OWN ROWS (2026-10-13): its Handed in list, filtered —
  // never a list of the whole cohort, which this page no longer has.
  const filterOf = (f: HandedFilter) => routes.assignment(courseId, cw.course_work_id, "handed", f);
  const tile = (k: string, label: string, hint: string, v: number | undefined | null,
                to: string, warn = false) => (
    <Link key={k} className={`asg-tile${v === 0 ? " zero" : ""}`} to={to} title={hint}>
      <span className="k">{label}</span>
      <span className={`v${warn && (v ?? 0) > 0 ? " warn" : ""}`}>{v ?? DASH}</span></Link>);
  return (
    <>
      {(tally?.review || (tally?.ready && access.can_release) || parked) ? (
        <div className="asg-needs" role="status">
          {(tally?.review ?? 0) > 0 && <span><b>{tally!.review}</b> waiting for your review</span>}
          {(tally?.ready ?? 0) > 0 && access.can_release && (
            <span><b>{tally!.ready}</b> ready to release or type into Classroom</span>)}
          {parked > 0 && <span><b>{parked}</b> parked — see why on Handed in</span>}
          <span className="spacer" />
          {(tally?.review ?? 0) > 0
            ? <Link className="btn sm" to={filterOf("review")}>Review them</Link>
            : (tally?.ready ?? 0) > 0 && access.can_release
              ? <Link className="btn sm" to={filterOf("ready")}>Open the ready ones</Link>
              : <Link className="btn sm" to={filterOf("parked")}>Show the parked ones</Link>}
        </div>) : null}
      {/* ⚠️ NO HANDED-IN TILE (owner, same day: "handed in no need"): the tab says it. */}
      <div className="asg-tiles" role="group" aria-label="Counts">
        {COUNTS.map((c) => tile(c.k, c.label, c.hint, tally ? tally[c.k] : null, filterOf(c.k),
                                c.k === "review"))}
        {tile("running", "running", "a run in progress, or waiting for a grader", running,
              filterOf("running"))}
      </div>
      <HowMarked courseId={courseId} cw={cw} access={access} r={r} s={s}
                 onConfigure={() => onShow("configure")} />
      <div className="asg-brief">
        <div className="asg-k">The brief <span className="asg-k-sub">as Classroom has it</span></div>
        {cw.description.trim()
          ? <div className="md"><Markdown text={cw.description} /></div>
          : <p className="small muted">Classroom has no description for this assignment.</p>}
      </div>
    </>);
}

/** How this assignment is marked, in sentences — every one read from what the setup saved, and
 *  the place to change it one click away.
 *  🔴 THE POINTS ARE THE SETUP'S OWN ARITHMETIC (`effectivePoints`, gated against the server's
 *  `rubric.effective`), so this line and the Evaluation tab cannot show one criterion two ways.
 *  ⚠️ THE TEACHER'S TESTS ARE ONE DATABASE READ, made here because the Overview is where somebody
 *  checks what an assignment proves before pressing Run. */
function HowMarked({ courseId, cw, access, r, s, onConfigure }: {
  courseId: string; cw: Assignment; access: CourseAccess;
  r: Async<Awaited<ReturnType<typeof getRubrics>>>;
  s: Async<Awaited<ReturnType<typeof getSettings>>>;
  onConfigure: () => void;
}) {
  const t = useAsync(() => getTeacherTests(courseId, cw.course_work_id),
                     [courseId, cw.course_work_id]);
  const b = cw.binding;
  const rubric = r.data?.rubrics.find((x) => x.id === (b?.rubric_id ?? r.data?.default_rubric_id));
  const ov = b?.criteria_overrides ?? {};
  const modeOf = (id: string) => (ov[id]?.mode ?? "score") as CriterionMode;
  const base = rubric?.criteria ?? [];
  const extras = (b?.extra_criteria ?? []).map((x, i) => ({ id: `X${i + 1}`,
    weight: Number(x.weight) || 0, label: x.label ?? "" }));
  const max = base.reduce((n, c) => n + (c.weight || 0), 0);
  const pts = new Map(effectivePoints(base, extras, modeOf, max).rows.map((x) => [x.id, x]));
  const tasks = b?.tasks?.length ?? 0;
  const crit = [...base.map((c) => ({ id: c.id, label: c.label ?? "" })), ...extras]
    .filter((c) => modeOf(c.id) !== "off");
  const engine = b?.engine_id || String(s.data?.settings.engine ?? "");
  const model = String(s.data?.settings.model ?? "");
  const effort = String(s.data?.settings.effort ?? "");
  const sysAuto = Boolean(s.data?.settings.auto_grade);
  const testing = b?.testing_required ?? rubric?.testing_required;
  // The teacher's tests, by the criterion each one proves.
  const on = new Map<string, { essential: boolean; n: number }>();
  for (const x of t.data?.items ?? []) {
    const k = criterionOf(x);
    const was = on.get(k) ?? { essential: false, n: 0 };
    on.set(k, x.kind === "brief" ? { ...was, essential: true } : { ...was, n: was.n + 1 });
  }
  const tests = [...on].map(([k, v]) => [
    v.essential ? "the essential tests" : "",
    v.n ? `${v.n} test${v.n === 1 ? "" : "s"} of yours` : "",
  ].filter(Boolean).join(" and ") + ` on ${k}`);
  return (
    <div>
      <div className="asg-k">How it is marked
        <button type="button" className="linkish asg-k-act" onClick={onConfigure}>Configure</button></div>
      <dl className="asg-facts">
        <dt>Criteria</dt>
        <dd>{crit.length === 0 ? <span className="muted">{DASH}</span>
          : crit.map((c, i) => {
              const p = pts.get(c.id);
              return (
                <span key={c.id}>{i > 0 && " · "}<b>{c.id}</b> {c.label}
                  {p?.mode === "advisory" ? " — advisory"
                    : p?.points != null ? ` — ${p.points}` : ""}
                  {c.id === "C1" && tasks > 0 && `, from ${tasks} task${tasks === 1 ? "" : "s"}`}
                </span>);
            })}</dd>
        <dt>Tests</dt>
        <dd>{t.error ? <span className="muted">could not be read just now</span>
          : !t.data ? <span className="muted">{DASH}</span>
          : tests.length ? tests.join(" · ") : "none of yours"}
          {" · "}{testing == null ? "students' own tests: not said"
            : testing ? "students' own tests expected" : "students' own tests not expected"}</dd>
        <dt>Grader</dt>
        <dd>{engine || <span className="muted">{DASH}</span>}
          {!b?.engine_id && engine && ", the cohort's default"}
          {model && ` · ${model}`}{effort && ` · ${effort} effort`}
          {b?.guidance ? " · with notes for the grader" : ""}</dd>
        <dt>Unattended</dt>
        <dd>{b?.auto_grade === true && sysAuto ? "on — new hand-ins are graded by themselves"
          : b?.auto_grade === true ? "armed here, but the cohort's switch is off"
          : "off — runs when somebody presses Run"}</dd>
        <dt>Marks</dt>
        <dd>{!cw.associated_with_developer
          ? "typed into Classroom by hand: this coursework was made in the Classroom UI"
          : !access.write_allowed ? "typed into Classroom by hand: this cohort is read-only"
          : "released to Classroom from this console"}</dd>
      </dl>
    </div>);
}

/** Rescan the cohort: every submission is considered again, and nothing is spent.
 *  ⚠️ `quiet` where the permission's sentence is already beside it (the Handed in row says it
 *  once for Run all), so it is not said twice on one line. */
function RescanButton({ courseId, may, onDone, quiet = false }: {
  courseId: string; may: boolean; onDone: () => void; quiet?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string>("");
  return (
    <div className="row gap">
      {msg && <span className="small muted">{msg}</span>}
      {!may && !quiet && <span className="small muted">{CAN_RUN}</span>}
      <button className="btn sm" disabled={busy || !may} title="🔴 `seen` is a change detector,
not a work queue: a submission is stamped the first time it is listed, so binding a rubric later
reaches nothing already seen. A rescan re-considers everything. The planner's gates still
apply, so this enqueues rather than force-grades."
        onClick={() => {
          setBusy(true); setMsg("");
          tracked(poll(courseId, { rescan: true, auto_grade: false }))
            .then((res) => setMsg(`considered ${res.considered}, enqueued ${res.enqueued}, ` +
                                  `parked ${res.parked} — nothing was spent`))
            .catch((err: unknown) => setMsg((err as ApiError).message))
            .finally(() => { setBusy(false); onDone(); });
        }}>Rescan (plan only)</button>
    </div>
  );
}

/**
 * The open assignment's place, when Classroom does not list it.
 *
 * 🔴 A LINK THAT OPENED NOTHING (2026-10-03). The listing asks Classroom for PUBLISHED and DRAFT
 * work and never DELETED, while a grade keeps the title the coursework cache holds — so a link to
 * a deleted assignment's title landed on the list with nothing open and nothing said. Its grades
 * are kept, with their reports, and since the Grades view went (2026-10-06) they are listed
 * right here rather than a link away.
 */
function Unlisted({ cw, title, grades }: {
  cw: string; title: string; grades: CohortRow[] | null;
}) {
  const held = grades?.length ?? null;
  const reportHref = useContext(ReportHref);
  useReportList((grades ?? []).map((g) => ({ sid: g.submission_id, student: g.student,
                                              title })), "kept for this assignment");
  return (
    <div className="stack tight">
      <div className="alert warn" role="status">
        <span className="ico">⚠️</span>
        <div className="body">
          <div className="t">Classroom does not list {title ? <>&ldquo;{title}&rdquo;</>
            : <code>{cw}</code>}</div>
          <div className="m">
            It may have been deleted in Classroom, so there are no submissions to open here.{" "}
            {held == null ? "The grades this console holds for it are still being read."
              : held === 0 ? "This console holds no grade for it."
              : <>This console still holds {held} grade{held === 1 ? "" : "s"} for it, kept with
                  {held === 1 ? " its report" : " their reports"}:</>}
          </div>
        </div>
      </div>
      {grades && grades.length > 0 && (
        <ul className="asg-kept">
          {grades.map((g) => (
            <li key={g.submission_id}>
              <Link className="asg-who" to={reportHref(g.submission_id)}>
                {g.student || "unnamed"}</Link>{" "}
              <span className="asg-mark">{g.points ?? DASH}<span className="muted">/
                {g.max_points ?? DASH}</span></span>{" "}
              <State s={g.status} />
            </li>))}
        </ul>)}
    </div>
  );
}

/** "2026-09-14" from Classroom's `{year, month, day}`, or "" — never a guessed time or zone. */
function dueOf(due: unknown): string {
  if (!due || typeof due !== "object") return "";
  const d = due as { year?: number; month?: number; day?: number };
  if (!d.year) return "";
  const pad = (n?: number) => String(n ?? 1).padStart(2, "0");
  return `${d.year}-${pad(d.month)}-${pad(d.day)}`;
}

/** "2026-09-27": the day an instant fell on, in the viewer's zone, or "". */
function dayOf(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(+d)) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The badge strip: what is bound, and the one fact that decides whether a mark can ever be
 *  written back to Google. */
function Badges({ cw, b }: { cw: Assignment; b: Assignment["binding"] }) {
  return (
    <>
      {/* ⚠️ "NOTES", NOT "BRIEF" (2026-10-05): the brief is Classroom's own description, shown on
          the setup's Brief tab; what this badge reports is the teacher's notes for the grader. */}
      {b?.guidance && <Badge tone="ok" dot title="This assignment has notes for the grader, read
with the brief and the base rubric.">notes</Badge>}
      {(b?.tasks?.length ?? 0) > 0 && (
        <Badge tone="plain">{b!.tasks!.length} task{b!.tasks!.length === 1 ? "" : "s"}</Badge>)}
      {b?.engine_id && <Badge tone="plain">{b.engine_id}</Badge>}
      {b?.auto_grade === true && <Badge tone="warn" dot title="Unattended grading is armed for
this assignment. The system switch must also be on; each alone does nothing.">auto</Badge>}
      {/* 🔴 FIXED AT CREATION WITH NO MIGRATION. A bot can never write a grade to coursework
          a teacher made in the Classroom UI — no field, no method, no scope. */}
      {!cw.associated_with_developer && (
        <Badge tone="held" title="Created in the Classroom UI, so Google's developer-project
rule makes every bot grade write PERMISSION_DENIED. Fixed at creation; no migration exists.
The autograder measures it fine — the mark is typed in by hand, from Ready.">
          enter by hand</Badge>)}
    </>
  );
}

function Publish({ courseId, cw, access, onChanged }: {
  courseId: string; cw: Assignment; access: CourseAccess; onChanged: () => void;
}) {
  const [preview, setPreview] = useState<Record<string, unknown> | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  // ⚠️ PUBLISHING NOTIFIES EVERY STUDENT ON THE COURSE, so it is `can_release` — and it is a
  // write to Classroom, which a read-only cohort refuses whoever asks.
  const why = !access.can_release
    ? "Publishing tells every student, so it needs the Release permission on this cohort."
    : !access.write_allowed ? "This cohort is read-only, so nothing here may publish to it."
    : "";
  const send = (confirm: boolean) => {
    setBusy(true); setErr(null);
    tracked(publishAssignment(courseId, cw.course_work_id, confirm))
      .then((res) => { if (confirm) { setPreview(null); onChanged(); } else setPreview(res); })
      .catch((x) => setErr(x as ApiError))
      .finally(() => setBusy(false));
  };
  return (
    <div className="stack tight">
      {err && <ErrorBox err={err} />}
      <div className="alert warn">
        <span className="ico">⚠️</span>
        <div className="body">
          <div className="t">This is a draft</div>
          <div className="m">Classroom holds no student submissions for a draft and refuses to
            list any, so there is nothing to run yet. Everything else — how it is marked, its
            tests and its notes — can be set now, under Configure.</div>
        </div>
      </div>
      <div className="row gap">
        <button className="btn sm" disabled={busy || why !== ""} onClick={() => send(false)}>
          Publish…</button>
        {preview && (
          <button className="btn primary sm" disabled={busy || why !== ""}
                  onClick={() => send(true)}>
            Yes, publish and notify students</button>)}
        {why && <span className="small muted">{why}</span>}
      </div>
      {preview && <p className="alert small">{String(preview.note ?? "")}</p>}
    </div>
  );
}

/**
 * One assignment, open: what Classroom lists for it, each row joined to the grade this console
 * holds — the mark, the status word, who reviewed it and the language it was made in.
 *
 * 🔴 TWO SOURCES ON ONE ROW, AND THE GRADE IS THE DATABASE'S. The listing is Classroom's and is
 * the only thing here that waits on Google; the mark beside it is the same `GET /cohort` row the
 * filters show, so the page cannot show one submission two ways.
 */
function Submissions({ courseId, cw, access, g, e, listVersion, held, onChanged, rescan }: {
  courseId: string; cw: Assignment; access: CourseAccess;
  g: Async<Awaited<ReturnType<typeof getCohort>>>;
  e: Async<Awaited<ReturnType<typeof getEntryQueue>>>;
  /** Bumped whenever the page's list changes — a Binder applied, a rescan, a publish — so this
   *  listing is asked again rather than kept from before the change. */
  listVersion: number;
  /** The server's sentence when a teacher's Run is held, else null. */
  held: string | null;
  onChanged: () => void;
  /** The cohort's Rescan, drawn beside Run all. */
  rescan?: ReactNode;
}) {
  const { data, error, reload } = useAsync(
    () => getSubmissions(courseId, cw.course_work_id),
    [courseId, cw.course_work_id, listVersion]);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string>("");
  const [outcomes, setOutcomes] = useState<QueueResult[]>([]);
  const [err, setErr] = useState<ApiError | null>(null);
  const [sort, setSort] = useState<Sort>({ key: "work", dir: 1 });
  // 🔴 THE FILTER IS IN THE ADDRESS (2026-10-13), so the Overview's counts and a link from another
  // page open this list filtered, and Back undoes a press.
  const [params, setParams] = useSearchParams();
  const asked = params.get("show") ?? "";
  const filter: HandedFilter | "all" = isHandedFilter(asked) ? asked : "all";
  const setFilter = (f: HandedFilter | "all") => {
    const next = new URLSearchParams(params);
    next.delete("show");
    // ⚠️ THE TAB, THEN THE CHIP — the order every link to a chip is written in (`routes`), so an
    // address the page writes is the one a link to it would.
    if (f !== "all") { next.delete("tab"); next.set("tab", "handed"); next.set("show", f); }
    setParams(next);
  };
  const live = useLive();
  const [reprocessing, setReprocessing] = useState<string | null>(null);

  if (error) return <ErrorBox err={error} onRetry={reload} />;
  if (!data) return <p className="muted small">Loading what Classroom lists…</p>;
  // 🔴 NO LIST AT ALL RATHER THAN A LIST THAT LIES (2026-10-03). Joined against nothing, every
  // row fell back to the listing's own mark with no reviewer and no language, a paid grade
  // Classroom no longer lists vanished, and no mark read as entered — all without a word.
  if (g.error || e.error) {
    return (
      <div className="stack tight">
        {g.error && <ErrorBox err={g.error} onRetry={g.reload} />}
        {e.error && <ErrorBox err={e.error} onRetry={e.reload} />}
      </div>);
  }

  // ⚠️ `null`, NOT `[]`, WHILE `/cohort` IS ON ITS WAY: only then may a row show the listing's
  // own mark. Once it has answered, a submission it holds no grade for has none.
  const all = joinSubmissions(data.rows, g.data?.rows ?? null, e.data?.rows ?? [],
                              cw.course_work_id);
  const run = (ids: string[], label: string) => {
    setBusy(label); setErr(null); setMsg(""); setOutcomes([]);
    // 🔴 ONE CALL CARRYING A LIST, NEVER N CALLS IN A LOOP.
    tracked(queueGrade(courseId, cw.course_work_id, ids))
      // 🔴 SHOW WHAT HAPPENED TO EACH ONE. The summary line alone cannot say WHY a
      // submission did not start, and "why" is the whole question a teacher has when it
      // does not. `detail` is the dispatcher's own sentence.
      .then((res) => { setMsg(res.note); setOutcomes(res.results); })
      .catch((x) => setErr(x as ApiError))
      // ⚠️ AND ASK THE LIVE FEED AT ONCE, so the row says Starting… rather than nothing for the
      // seconds until the next poll would have.
      .finally(() => { setBusy(null); reload(); onChanged(); live.poke(); });
  };
  // 🔴 TAKE BACK WHAT HAS NOT STARTED (owner, 2026-10-07): one row, or — with no ids — every
  // queued run on this assignment, which the server reads from the same list the feed shows.
  const cancel = (ids: string[] | undefined, label: string) => {
    setBusy(label); setErr(null); setMsg(""); setOutcomes([]);
    tracked(cancelQueued(courseId, cw.course_work_id, ids))
      .then((res) => setMsg(res.note))
      .catch((x) => setErr(x as ApiError))
      .finally(() => { setBusy(null); reload(); onChanged(); live.poke(); });
  };
  const reprocess = (attemptId: string) => {
    setReprocessing(attemptId); setErr(null);
    tracked(reprocessRun(courseId, attemptId))
      .then((res) => setMsg(res.note))
      .catch((x) => setErr(x as ApiError))
      .finally(() => { setReprocessing(null); live.poke(); });
  };

  const runnable = data.rows.filter((r) => r.runnable);
  const inFlight = new Set([...(live.feed?.runs ?? []), ...(live.feed?.queued ?? [])]
    .map((x) => x.submission_id));
  const shown = sorted(rows(all, filter, inFlight), keyOf(sort.key), sort.dir);
  const queuedHere = (live.feed?.queued ?? [])
    .filter((q) => q.course_work_id === cw.course_work_id).length;
  return <SubmissionsList {...{ courseId, cw, access, data, all, shown, held, busy, msg, outcomes,
                                err, sort, setSort, filter, setFilter, inFlight, reprocessing,
                                runnable, run, reprocess, cancel, queuedHere, rescan,
                                onEntered: onChanged }} />;
}

/** The open assignment's list itself, once Classroom's listing and the grades are joined. Its own
 *  component, so it can tell the page which rows it shows — which a hook after the early returns
 *  above could not. */
function SubmissionsList({ courseId, cw, access, data, all, shown, held, busy, msg, outcomes, err,
                           sort, setSort, filter, setFilter, inFlight, reprocessing, runnable,
                           run, reprocess, cancel, queuedHere, rescan, onEntered }: {
  courseId: string; cw: Assignment; access: CourseAccess;
  data: Awaited<ReturnType<typeof getSubmissions>>;
  all: MarkRow[]; shown: MarkRow[]; held: string | null; busy: string | null; msg: string;
  outcomes: QueueResult[]; err: ApiError | null; sort: Sort; setSort: (s: Sort) => void;
  filter: HandedFilter | "all"; setFilter: (f: HandedFilter | "all") => void;
  inFlight: Set<string>;
  reprocessing: string | null; runnable: SubmissionRow[];
  run: (ids: string[], label: string) => void; reprocess: (attemptId: string) => void;
  cancel: (ids: string[] | undefined, label: string) => void; queuedHere: number;
  rescan?: ReactNode;
  onEntered: () => void;
}) {
  // 🔴 UNDER READY, EACH ROW CARRIES WHAT TYPING THE MARK INTO CLASSROOM TAKES (2026-10-13): the
  // column the cohort-wide Ready list had, now this assignment's.
  const ready = filter === "ready";
  const act = useEntryActions(courseId, onEntered);
  // ‹ › in the sheet walk this list as it is sorted and filtered, and only rows with a report —
  // and under a grade chip the sheet says which list it is walking.
  useReportList(shown.filter((r) => markOf(r)).map((r) => ({
    sid: r.submission_id, student: labelOf(r), title: cw.title })),
    isMarkingFilter(filter) ? `${FILTER_NOUN[filter]} on this assignment` : "on this assignment");
  const may = access.can_run_grading;
  return (
    <div className="stack asg-subs">
      {err && <ErrorBox err={err} />}
      {msg && <p className="alert info small">{msg}</p>}
      {outcomes.length > 0 && (
        <ul className="stack tight small asg-outcomes">
          {outcomes.map((o) => (
            <li key={o.submission_id}>
              <Badge tone={o.status === "started" ? "ok"
                         : o.status === "refused" ? "warn"
                         : o.status === "held" ? "held" : "plain"}>
                {o.status}
              </Badge>{" "}
              {/* ⚠️ THE SAME LABEL AS THE TABLE BELOW. These used to be a full-length id here
                  and a 10-char truncation there, so an outcome could not be paired with its
                  row by eye. */}
              <b>{who(data.rows.find((r) => r.submission_id === o.submission_id))}</b>{" "}
              <span className="muted">{o.detail}</span>
            </li>
          ))}
        </ul>)}

      <div className="row wrap gap">
        <span className="small">
          <b>{String(data.counts.submitted)}</b> handed in
          <span className="muted"> ({String(data.counts.turned_in)} turned in,{" "}
            {String(data.counts.returned)} returned)</span>
        </span>
        <span className="spacer" />
        {!may && <span className="small muted">{CAN_RUN}</span>}
        {rescan}
        {/* 🔴 THE SERVER'S SENTENCE WHEN IT HOLDS A TEACHER'S RUN, beside the button it disables:
            pressing it would park every submission on a hold this page already knows about. */}
        {may && held && <span className="small muted">{held}</span>}
        {/* 🔴 NO CHECKBOXES, AND THE REASON STANDS: every refusal is per row and server-side,
            so the set that runs is not the set that was ticked. Filters narrow what you LOOK
            at; they never decide what runs. */}
        <button className="btn sm"
                disabled={!may || held != null || busy != null || runnable.length === 0}
                onClick={() => run(runnable.map((r) => r.submission_id), "all")}>
          Run all {runnable.length}</button>
        {/* 🔴 BESIDE THE BUTTON THAT QUEUED THEM, and only while something is queued: the count
            is the live feed's, the list the server cancels from. */}
        {queuedHere > 0 && (
          <button className="btn sm" disabled={!may || busy != null}
                  onClick={() => cancel(undefined, "cancel-all")}>
            {busy === "cancel-all" ? "Cancelling…" : `Cancel ${queuedHere} queued`}</button>)}
      </div>

      {/* 🔴 THE CHIPS ARE THIS ASSIGNMENT'S IN REVIEW, READY, RELEASED OR ENTERED, HELD AND RUNNING
          (2026-10-13) — the words the cohort-wide strip used — then what only a listing can say.
          Each count is the length of the list it opens: one predicate, `rows`. */}
      <div className="row wrap gap" role="group" aria-label="Show">
        <button className={`chip${filter === "all" ? " on" : ""}`} aria-pressed={filter === "all"}
                title="Every submission on this assignment." onClick={() => setFilter("all")}>
          all<span className="cnt">{all.length}</span></button>
        {FILTERS.map((f) => {
          const n = rows(all, f.id, inFlight).length;
          if (f.id === "unlisted" && n === 0) return null;
          return (
            <button key={f.id} className={`chip${filter === f.id ? " on" : ""}`}
                    title={f.hint} aria-pressed={filter === f.id}
                    onClick={() => setFilter(filter === f.id ? "all" : f.id)}>
              {f.label}<span className="cnt">{n}</span>
            </button>);
        })}
      </div>
      {/* Said once, above the rows it is about. */}
      {ready && !access.can_release && <p className="small muted">{CAN_ENTER}</p>}

      {shown.length === 0
        ? <Empty title="Nothing matches this filter"
                 hint="Clear the filter to see every submission on this assignment." />
        : (
        <div className="tablewrap asg-table">
          {/* 🔴 `className="tb"`. Without it this table gets NONE of the design system —
              index.css defines only `table.tb` and has no bare `table` rule — so it rendered
              as a raw browser default inside a styled card. One attribute, and it was most
              of "the submissions presentation is terrible". */}
          <table className="tb">
            <thead><tr>
              <Th label="Student" k="student" sort={sort} setSort={setSort} />
              <Th label="Handed in" k="handed" sort={sort} setSort={setSort} />
              {/* 🔴 UNDER READY, INTO CLASSROOM TAKES THIS COLUMN'S PLACE (the approved mockup),
                  and says in its cell what Classroom already holds: beside the list there is no
                  room for both — [X] measured, the table 795px in a 740px column, Run cut off. */}
              {!ready && <Th label="Classroom" k="classroom" sort={sort} setSort={setSort} right
                  title="The mark Classroom already holds — which may be one a teacher typed in
                         by hand, and is never one this pipeline wrote." />}
              <Th label="Mark" k="mark" sort={sort} setSort={setSort} right
                  title="The mark that goes out: the reviewed one where a review is in force." />
              {/* ⚠️ THE LANGUAGE IS UNDER THE STATUS, NOT A COLUMN (2026-10-13): beside the list
                  of assignments every column costs, and the list had to widen for its titles. */}
              <Th label="Status" k="status" sort={sort} setSort={setSort} />
              {ready && <th>Into Classroom</th>}
              {/* 🔴 ONE COLUMN FOR WHAT IS HAPPENING AND WHAT YOU CAN DO ABOUT IT (2026-10-06): it
                  replaces the Queue column, which showed the machine word. */}
              <th>Run</th>
            </tr></thead>
            <tbody>
              {shown.map((r) => <SubRow key={r.submission_id} r={r} busy={busy != null}
                                        may={may} held={held}
                                        reprocessing={reprocessing}
                                        onReprocess={reprocess}
                                        cancelling={busy === `cancel:${r.submission_id}`}
                                        onCancel={() => cancel([r.submission_id],
                                                               `cancel:${r.submission_id}`)}
                                        onRun={() => run([r.submission_id], r.submission_id)}
                                        into={ready ? <IntoClassroom e={r.entry ?? undefined}
                                                        student={labelOf(r)}
                                                        may={access.can_release} act={act} />
                                          : undefined} />)}
            </tbody>
          </table>
        </div>)}
    </div>
  );
}

/** The label for one row. 🔴 NEVER the raw `userId` — that is a Google account identifier.
 *  Name when the roster is synced; otherwise the repository owner, which the student chose
 *  and published themselves and which a teacher actually recognises. */
function who(r?: SubmissionRow): string {
  if (!r) return DASH;
  if (r.student) return r.student;
  const owner = ownerOf(r.links[0]);
  return owner ? `@${owner}` : r.submission_id.slice(0, 8);
}

/** `https://github.com/0xhaz/Async-Pre-Builders-Tasks/tree/main/x` -> `0xhaz`. */
function ownerOf(url?: string): string {
  if (!url) return "";
  try {
    const u = new URL(url);
    if (u.hostname !== "github.com") return "";
    return u.pathname.split("/").filter(Boolean)[0] ?? "";
  } catch { return ""; }
}

/** One joined row's label: Classroom's, or the grade's own student when Classroom no longer
 *  lists it. */
function labelOf(r: MarkRow): string {
  return r.sub ? who(r.sub) : (r.grade?.student || "unnamed");
}

const FILTERS: { id: HandedFilter; label: string; hint: string }[] = [
  { id: "review", label: "in review", hint: STATUS_HINT.REVIEW! },
  { id: "ready", label: "ready",
    hint: "Measured and reviewed, and not yet released or typed into Classroom." },
  { id: "done", label: "released or entered",
    hint: "The student has the mark: released by this console, or typed into Classroom by hand." },
  { id: "held", label: "held", hint: STATUS_HINT.HELD! },
  // ⚠️ ALWAYS DRAWN, EVEN AT 0: it comes and goes as runs do, and a chip that appears and vanishes
  // moves every chip after it under the reader's pointer.
  { id: "running", label: "running",
    hint: "Being graded now, or waiting its turn — it moves on its own." },
  { id: "ungraded", label: "not graded here",
    hint: "No grade in this console yet." },
  { id: "parked", label: "parked",
    hint: "In the queue for a reason that needs somebody — not waiting its turn, and not a run in flight." },
  { id: "marked", label: "marked in Classroom",
    hint: "Classroom already holds a mark for this submission." },
  { id: "unlisted", label: "not listed by Classroom",
    hint: "A grade this console holds for a submission Classroom no longer lists." },
];

/**
 * The rows a chip shows. `inFlight` is every submission the live feed has running or queued —
 * the feed's, never the queue entry's word, which says `dispatched` long after a run has ended.
 */
export function rows(all: MarkRow[], f: string,
                     inFlight: ReadonlySet<string> = new Set()): MarkRow[] {
  switch (f) {
    // 🔴 THE STATUS WORD, NOT `escalate`: a reviewed grade is still escalated and is no longer
    // waiting for anybody.
    case "review": return all.filter((r) => markOf(r)?.status === "REVIEW");
    case "ready": return all.filter(isReadyRow);
    // The Overview's own words: released by this console or typed in by hand, as `tallies` counts.
    case "done": return all.filter((r) => markOf(r)?.status === "RELEASED" || isEntered(r));
    case "held": return all.filter((r) => markOf(r)?.status === "HELD");
    case "ungraded": return all.filter((r) => !markOf(r));
    case "marked": return all.filter((r) => r.sub?.history.grade_last?.points_earned != null);
    case "parked": return all.filter((r) => isParked(r.sub?.queue));
    case "running": return all.filter((r) => inFlight.has(r.submission_id));
    case "unlisted": return all.filter((r) => !r.sub);
    default: return all;
  }
}

function keyOf(k: string): (r: MarkRow) => number | string | null | undefined {
  switch (k) {
    case "student": return (r) => labelOf(r).toLowerCase();
    case "handed": return (r) => r.sub?.history.turned_in_at ?? null;
    case "classroom": return (r) => r.sub?.history.grade_last?.points_earned ?? null;
    case "mark": return (r) => markOf(r)?.points ?? null;
    case "status": return (r) => markOf(r)?.status ?? null;
    // 🔴 WORK FIRST, THEN BY NAME: a grade waiting for review, a mark to type, a parked run,
    // something not yet graded — and only then what is done. Google's pagination order is not
    // an order, and the old default's comment promised this while its code sorted by name.
    default: return (r) => `${workRank(r)}:${labelOf(r).toLowerCase()}`;
  }
}

function SubRow({ r, busy, may, held, reprocessing, onReprocess, onRun, cancelling,
                  onCancel, into }: {
  r: MarkRow; busy: boolean; may: boolean; held: string | null;
  reprocessing: string | null; onReprocess: (attemptId: string) => void; onRun: () => void;
  cancelling: boolean; onCancel: () => void;
  /** Under the Ready filter, the Into Classroom cell. */
  into?: ReactNode;
}) {
  const s = r.sub;
  const m = markOf(r);
  const owner = ownerOf(s?.links[0]);
  const live = useLive();
  const lit = useContext(JustNow).has(r.submission_id);
  // The mark Classroom already holds, if any — its own column, or under Ready a line of the cell
  // that takes that column's place.
  const classroom = s?.history.grade_last?.points_earned != null ? s.history.grade_last : null;
  // ⚠️ A RE-GRADE DIMS THE MARK IT WILL REPLACE rather than hiding it: until the new run lands,
  // the old mark is the grade.
  const regrading = Boolean(live.feed?.runs.some(
    (x) => x.submission_id === r.submission_id && x.regrade && x.stage !== "held"));
  // ⚠️ ONLY THE NAME AND THE MARK ARE LINKS, not the whole row: a row carries a Run button, and
  // a click that might either open a report or spend money is not a click anyone should have
  // to aim. And only when there is a grade — an ungraded submission has no report to open.
  const reportHref = useContext(ReportHref);
  const report = m ? reportHref(r.submission_id) : null;
  return (
    <tr className={lit ? "just" : undefined}>
      <td>
        {report
          ? <Link className="asg-who" to={report}
                  title="Open the grade, its citations and what the grader did">{labelOf(r)}</Link>
          : <b>{labelOf(r)}</b>}
        <div className="small">
          {s?.links[0] && <a href={s.links[0]} target="_blank" rel="noreferrer">
            {owner ? "repo" : "link"}</a>}
          {s?.history.resubmitted ? (
            <span className="muted" title={`${s.history.turn_ins} hand-ins; the timestamp
                                            shown is the latest.`}> · resubmitted</span>
          ) : null}
          {/* 🔴 A GRADE CLASSROOM NO LONGER LISTS. It was paid for and it is held; hiding it
              would hide a result, and showing it as an ordinary row would claim Classroom still
              has the submission. */}
          {!s && <Badge tone="held" title="This console holds a grade for a submission Classroom
no longer lists — it may have been deleted or un-submitted. The grade and its report are kept.">
            not listed by Classroom</Badge>}
        </div>
      </td>
      {/* 🔴 `<State>`, not a bare `.badge`. `.badge` alone sets `border-color: transparent`
          with no background and no colour — a pill-shaped nothing. Every other state surface
          in the app goes through the tone map. */}
      {/* ⚠️ THE STATE ONLY WHEN IT IS NOT THE ORDINARY ONE. TURNED_IN on every row is the
          column saying the same word fourteen times; a date already says it was handed in,
          and RETURNED, CREATED or a withdrawal is what a reader is scanning for. */}
      <td className="small">
        {s ? <>
            {s.history.turned_in_at
              ? <When iso={s.history.turned_in_at as string | null} />
              : <span className="muted">not handed in</span>}
            {(s.state !== "TURNED_IN" || s.second_opinion || s.history.withdrawn) && (
              <div className="asg-cellbadges">
                {s.state !== "TURNED_IN" && <State s={s.state} />}
                {s.second_opinion && <Badge tone="warn" title="Already marked and handed back.
A run here is a second opinion, and it cannot overwrite what the teacher decided.">
                  second opinion</Badge>}
                {s.history.withdrawn && <Badge tone="stop">withdrawn</Badge>}
              </div>)}
          </>
          : <span className="muted">{DASH}</span>}
      </td>
      {/* The mark Classroom already holds. ⚠️ IT IS AN OBJECT, `{points_earned, max_points,
          at}`. `String()` accepts anything, so rendering it whole typechecked cleanly and put
          `[object Object]` in front of a teacher for every one of 38 rows. */}
      {into === undefined && (
        <td className="num small r" title={s?.history.grade_last?.at ?? undefined}>
          {classroom != null
            ? <><b>{classroom.points_earned}</b>
                {classroom.max_points != null &&
                  <span className="muted">/{classroom.max_points}</span>}</>
            : <span className="muted">{DASH}</span>}
        </td>)}
      <td className="r">
        {m && m.points == null
          ? <span className="muted" title="Nothing could be measured. Posting that as a zero would
render our failure as the student's.">{DASH}</span>
        : m && report
          ? <>
              <Link className={regrading ? "asg-mark dimmed" : "asg-mark"} to={report}
                    title="Open the grade, its citations and what the grader did">
                {m.points ?? DASH}<span className="muted">/{m.max_points ?? DASH}</span></Link>
              {regrading && <div className="small muted">being re-graded</div>}
              {/* 🔴 THE MARK THAT GOES OUT IS THE REVIEWED ONE, and the agent's stays beside
                  it, so a moved mark is visible in the list rather than only on the report. */}
              {m.agent_points !== m.points && (
                <div className="small muted">agent {m.agent_points ?? DASH}</div>)}
            </>
          : <span className="muted">{DASH}</span>}
        {/* Under the mark it is about, as on the mock. */}
        {lit && <span className="justnow">Graded just now</span>}
      </td>
      <td>
        {m ? <>
               <div className="asg-cellbadges">
                 <State s={m.status} />
                 {isEntered(r) && <Badge tone="ok" title="Recorded here as typed into Classroom
by hand. Nothing was written to Google by this console.">entered</Badge>}
               </div>
               {/* ⚠️ WHO REVIEWED IT, BESIDE THE WORD THE REVIEW DECIDED. The address before the
                   @ on the row — the whole of it is the title, and the report names them. */}
               {m.reviewed_by && (
                 <div className="small muted asg-reviewer" title={`Reviewed by ${m.reviewed_by}`}>
                   reviewed · {m.reviewed_by.split("@")[0]}</div>)}
               {/* The language it was graded in; a grade from before 2026-09-27 has none. */}
               {m.language && <div className="small muted">{m.language}</div>}
             </>
           : <span className="muted small">not graded</span>}
      </td>
      {/* 🔴 WHAT IS HAPPENING, IN WORDS, AND THE BUTTON THAT FITS IT (2026-10-06). A park's
          sentence is the dispatcher's own, behind `Why` — reachable on a tap and by a screen
          reader, which the old hover-only reason was not. */}
      {into !== undefined && (
        <td>{into}
          {classroom != null && (
            <div className="small muted" title={classroom.at ?? undefined}>
              Classroom holds {classroom.points_earned}
              {classroom.max_points != null && `/${classroom.max_points}`}</div>)}
        </td>)}
      <td>
        <RunCell sid={r.submission_id} graded={Boolean(m)} queue={s?.queue ?? null}
                 runnable={s ? s.runnable : undefined} unlisted={!s} may={may} busy={busy}
                 held={held} reprocessing={reprocessing != null} onRun={onRun}
                 onReprocess={onReprocess} cancelling={cancelling} onCancel={onCancel} />
      </td>
    </tr>
  );
}
